import {
  createSignal,
  createEffect,
  createMemo,
  createResource,
  onCleanup,
  Show,
  batch,
} from "solid-js";
import { useNavigate } from "@solidjs/router";
import styles from "./Inventory.module.css";
import Text from "../components/Text";
import { useTranslation } from "../hooks/useTranslation";
import { useToast } from "../components/MessageBox";
import { useApi, isOk } from "../api";
import CraftingTaskPanel from "../components/CraftingTaskPanel";
import { type CraftingResolverService, type ItemDef } from "../services/craftingResolver";
import { useCraftingResolver } from "../services/craftingResolverContext";
import { useItemName } from "../hooks/useItemName";
import {
  useInventoryApi,
  type StorageInfo,
  type StorageSnapshot,
  type PricingMap,
  type ResolveResult,
  type ItemStackDto,
  type CustomItemDefinition,
} from "../services/inventoryApi";
import { subscribeInventoryEvents } from "../services/inventoryEvents";

import StateDisplay from "../components/StateDisplay";
import { useActiveBot } from "../state/activeBot";
import InventoryPanel from "../components/inventory/InventoryPanel";
import ItemDetailCard from "../components/inventory/ItemDetailCard";
import MoveConfirmDialog from "../components/inventory/MoveConfirmDialog";
import {
  loadViewState,
  saveViewState,
  computeSlots,
  customItemToStackDto,
  selectCraftableCustomItems,
  toCustomItemEntry,
  type SortMode,
  type SlotData,
  type CustomItemEntry,
  type DragSelection,
} from "../components/inventory/inventoryUtils";
import { firstError } from '../api/envelope';


export default function Inventory() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const api = useApi();
  const inv = useInventoryApi();
  const resolver = useCraftingResolver();
  const { itemName } = useItemName();
  const savedView = loadViewState();

  const [loading, setLoading] = createSignal(true);
  const [storages, setStorages] = createSignal<StorageInfo[]>([]);
  const [allStorage, setAllStorage] = createSignal<StorageSnapshot>({});
  const { botId: activeBotId } = useActiveBot();
  const preferredBotId = () => activeBotId();
  const [pricing, setPricingSig] = createSignal<PricingMap>({});
  const [profitMargin, setProfitMargin] = createSignal<number | null>(null);
  const [selected, setSelected] = createSignal<ItemDef | null>(null);
  const [selectedPanel, setSelectedPanel] = createSignal<"left" | "right">("left");
  const [dualView, setDualView] = createSignal(savedView.dualView);

  const [storageCache, setStorageCache] = createSignal<Record<string, StorageSnapshot>>({});
  const [reservationCache, setReservationCache] = createSignal<Record<string, StorageSnapshot>>({});

  // Keyed by ItemKey, which already carries the NBT hash, so one entry describes the item wherever
  // it is stocked. Kept after a stack drops to zero: the same key coming back needs no refetch.
  const [seenStacks, setSeenStacks] = createSignal<Record<string, ItemStackDto>>({});

  function rememberStacks(stacks: ItemStackDto[]) {
    if (stacks.length === 0) return;
    setSeenStacks(prev => {
      const next = { ...prev };
      for (const stack of stacks) next[stack.key] = stack;
      return next;
    });
  }

  const [knownCustomItems, setKnownCustomItems] = createSignal<CustomItemDefinition[]>([]);

  // items.json lists only the metadata variants it knows, so a plain vanilla key it never enumerates
  // (every potion damage value, for one) has no catalog tile either and needs the stack to carry its
  // own definition, the same way an NBT item does.
  const customItems = createMemo<CustomItemEntry[]>(() => {
    const res = resolver();
    if (!res) return [];
    const byKey: Record<string, ItemStackDto> = {};
    for (const definition of knownCustomItems()) {
      const dto = customItemToStackDto(definition);
      byKey[dto.key] = dto;
    }
    // A stocked stack says what a storage actually holds, so it wins over the registry's description.
    for (const [key, stack] of Object.entries(seenStacks())) byKey[key] = stack;
    const entries = Object.values(byKey)
      .filter(dto => dto.isCustom || !res.getItem(dto.key))
      .map(dto => toCustomItemEntry(dto, res));
    return selectCraftableCustomItems(entries, allStorage(), (name, itemId) => res.hasCustomRecipe(name, itemId));
  });

  const selectedStack = () => customItems().find(entry => entry.def.key === selected()?.key)?.stack;

  function getSnap(id: string | null): StorageSnapshot {
    return id ? (storageCache()[id] ?? {}) : {};
  }
  function getRes(id: string | null): StorageSnapshot {
    return id ? (reservationCache()[id] ?? {}) : {};
  }
  function updateStorageCache(id: string, snap: StorageSnapshot) {
    setStorageCache(prev => ({ ...prev, [id]: snap }));
  }
  function updateReservationCache(id: string, res: StorageSnapshot) {
    setReservationCache(prev => ({ ...prev, [id]: res }));
  }

  const [dragData, setDragData] = createSignal<DragSelection | null>(null);
  const [dropTarget, setDropTarget] = createSignal<"left" | "right" | null>(null);
  const [pendingMove, setPendingMove] = createSignal<{
    sourceStorageId: string;
    sourceStorageName: string;
    destinationStorageId: string;
    destinationStorageName: string;
    items: DragSelection["items"];
  } | null>(null);

  const [selectedKeys, setSelectedKeys] = createSignal<Set<string>>(new Set());
  const [rangeAnchor, setRangeAnchor] = createSignal<string | null>(null);

  const panelSlots = (panel: "left" | "right") => panel === "left" ? leftSlots() : rightSlots();

  function selectItem(panel: "left" | "right", itemKey: string, event: MouseEvent | KeyboardEvent) {
    const slots = panelSlots(panel);
    const slot = slots.find(s => s.item.key === itemKey);
    if (!slot) return;

    const keys = new Set(selectedKeys());
    const anchor = selectedPanel() === panel ? rangeAnchor() : null;

    batch(() => {
      if (event.shiftKey && anchor) {
        const order = slots.map(s => s.item.key);
        const from = order.indexOf(anchor);
        const to = order.indexOf(itemKey);
        if (from >= 0 && to >= 0) {
          const range = order.slice(Math.min(from, to), Math.max(from, to) + 1);
          setSelectedKeys(new Set<string>(event.ctrlKey || event.metaKey ? [...keys, ...range] : range));
        }
      } else if ((event.ctrlKey || event.metaKey) && selectedPanel() === panel) {
        if (keys.has(itemKey)) keys.delete(itemKey);
        else keys.add(itemKey);
        setSelectedKeys(keys);
        setRangeAnchor(itemKey);
      } else {
        setSelectedKeys(new Set<string>([itemKey]));
        setRangeAnchor(itemKey);
      }
      setSelected(slot.item);
      setSelectedPanel(panel);
    });
  }

  function clearSelection() {
    batch(() => {
      setSelectedKeys(new Set<string>());
      setRangeAnchor(null);
    });
  }

  const [changedKeys, setChangedKeys] = createSignal<Map<string, "up" | "down">>(new Map());

  const [resolveCache, setResolveCache] = createSignal<Record<string, Record<string, ResolveResult>>>({});

  function getResolveData(id: string | null): Record<string, ResolveResult> {
    return id ? (resolveCache()[id] ?? {}) : {};
  }

  const resolveInFlight = new Set<string>();
  const resolveQueued = new Map<string, string[]>();
  const lastResolveRequest = new Map<string, { snapshot: StorageSnapshot | undefined; keys: string }>();

  // Every storage change in the tenant arrives over SSE; only this storage's own changes may re-resolve it.
  function requestResolveData(storageId: string, snapshot: StorageSnapshot | undefined, res: CraftingResolverService) {
    const itemKeys = resolvableKeys(res, snapshot ?? {});
    const keySignature = itemKeys.join(",");
    const last = lastResolveRequest.get(storageId);
    if (last && last.snapshot === snapshot && last.keys === keySignature) return;
    lastResolveRequest.set(storageId, { snapshot, keys: keySignature });
    void fetchResolveData(storageId, itemKeys);
  }

  async function fetchResolveData(storageId: string, itemKeys: string[]) {
    if (itemKeys.length === 0) return;
    if (resolveInFlight.has(storageId)) {
      resolveQueued.set(storageId, itemKeys);
      return;
    }
    resolveInFlight.add(storageId);
    try {
      const data = await inv.resolveBatch(storageId, itemKeys);
      setResolveCache(prev => ({ ...prev, [storageId]: { ...(prev[storageId] ?? {}), ...data } }));
    } catch {
    } finally {
      resolveInFlight.delete(storageId);
      const queuedKeys = resolveQueued.get(storageId);
      if (queuedKeys) {
        resolveQueued.delete(storageId);
        void fetchResolveData(storageId, queuedKeys);
      }
    }
  }

  const [leftStorageId, setLeftStorageId] = createSignal<string | null>(savedView.leftStorageId);
  const [leftSearch, setLeftSearch] = createSignal(savedView.leftSearch);
  const [leftSort, setLeftSort] = createSignal<SortMode>(savedView.leftSort);
  const [leftSlots, setLeftSlots] = createSignal<SlotData[]>([]);

  const [rightStorageId, setRightStorageId] = createSignal<string | null>(savedView.rightStorageId);
  const [rightSearch, setRightSearch] = createSignal(savedView.rightSearch);
  const [rightSort, setRightSort] = createSignal<SortMode>(savedView.rightSort);
  const [rightSlots, setRightSlots] = createSignal<SlotData[]>([]);

  createEffect(() => {
    saveViewState({
      dualView: dualView(),
      leftStorageId: leftStorageId(),
      rightStorageId: rightStorageId(),
      leftSort: leftSort(),
      rightSort: rightSort(),
      leftSearch: leftSearch(),
      rightSearch: rightSearch(),
    });
  });

  createEffect(() => {
    const q = leftSearch();
    const lid = leftStorageId();
    const s = getSnap(lid);
    const a = allStorage();
    const r = getRes(lid);
    const rd = getResolveData(lid);
    const sort = leftSort();
    const changed = changedKeys();
    // Subscribe to cache signals for reactivity — SolidJS requires reads inside the effect body.
    void storageCache(); void reservationCache(); void resolveCache();
    const res = resolver();
    if (!res) return;
    setLeftSlots(computeSlots(q, s, a, r, rd, sort, res, changed, lid, customItems()));
  });

  createEffect(() => {
    const dual = dualView();
    const q = rightSearch();
    const rid = rightStorageId();
    const s = getSnap(rid);
    const a = allStorage();
    const r = getRes(rid);
    const rd = getResolveData(rid);
    const sort = rightSort();
    const changed = changedKeys();
    void storageCache(); void reservationCache(); void resolveCache();
    const res = resolver();
    if (!res || !dual) return;
    setRightSlots(computeSlots(q, s, a, r, rd, sort, res, changed, rid, customItems()));
  });

  createEffect(() => {
    const lid = leftStorageId();
    const res = resolver();
    if (!lid || !res) return;
    requestResolveData(lid, storageCache()[lid], res);
  });

  createEffect(() => {
    const rid = rightStorageId();
    const res = resolver();
    if (!rid || !res || !dualView()) return;
    requestResolveData(rid, storageCache()[rid], res);
  });

  // Stocked vanilla keys plus every custom item the search may list, stocked or not, so their tiles
  // carry the same Craft badge a vanilla tile gets.
  function resolvableKeys(res: CraftingResolverService, snap: StorageSnapshot): string[] {
    return [
      ...res.getAllItems().filter(i => (snap[i.key] ?? 0) > 0).map(i => i.key),
      ...customItems().map(entry => entry.def.key),
    ];
  }

  createEffect(() => {
    if (!resolver()) return;

    setLoading(true);
    void (async () => {
      try {
        const storageList = await inv.fetchStorages();
        setStorages(storageList);

        // Prices and margin belong to the tenant; without a team the storage view still works.
        try {
          const [pricingData, tenantSettings] = await Promise.all([inv.fetchPricing(), inv.fetchTenantSettings()]);
          setPricingSig(pricingData);
          setProfitMargin(tenantSettings.defaultProfitMargin);
        } catch { }

        inv.fetchCustomItems().then(setKnownCustomItems).catch(() => {});

        const allItems = await inv.fetchAllStorageItems();
        setAllStorage(allItems.quantities);
        rememberStacks(allItems.stacks);

        const leftId = savedView.leftStorageId && storageList.find((s) => s.id === savedView.leftStorageId)
          ? savedView.leftStorageId
          : storageList.length > 0 ? storageList[0].id : null;

        if (leftId) {
          await loadStorageData(leftId);
          setLeftStorageId(leftId);
        }

        if (savedView.dualView) {
          const rightId = savedView.rightStorageId && storageList.find((s) => s.id === savedView.rightStorageId)
            ? savedView.rightStorageId
            : storageList.length > 1 ? storageList[1].id : null;
          if (rightId) {
            await loadStorageData(rightId);
            setRightStorageId(rightId);
          }
        }
      } catch (e: any) {
        toast?.error(e?.message ?? t("INVENTORY_LOAD_FAILED"));
      }
      setLoading(false);
    })();
  });

  let unsubSSE: (() => void) | undefined;
  createEffect(() => {
    unsubSSE?.();
    unsubSSE = undefined;

    unsubSSE = subscribeInventoryEvents((evt) => {
      if (evt.type === "inventory_changed") {
        const { storageId, snapshot } = evt.payload as { storageId: string; snapshot: StorageSnapshot };

        const cache = storageCache();
        if (storageId in cache) {
        const oldSnap = cache[storageId];
        const changed = new Map<string, "up" | "down">();
        const allKeys = new Set([...Object.keys(oldSnap), ...Object.keys(snapshot)]);
        for (const k of allKeys) {
          const oldQty = oldSnap[k] ?? 0;
          const newQty = snapshot[k] ?? 0;
          if (oldQty !== newQty) changed.set(`${storageId}:${k}`, newQty > oldQty ? "up" : "down");
        }
        if (changed.size > 0) {
          setChangedKeys(prev => {
            const merged = new Map(prev);
            for (const [k, dir] of changed) merged.set(k, dir);
            return merged;
          });
          setTimeout(() => {
            setChangedKeys(prev => {
              const next = new Map(prev);
              for (const k of changed.keys()) next.delete(k);
              return next;
            });
          }, 700);
        }
        }

        updateStorageCache(storageId, snapshot);
        inv.fetchAllStorageItems()
          .then(r => { setAllStorage(r.quantities); rememberStacks(r.stacks); })
          .catch(() => {});
      }
      if (evt.type === "reservations_changed") {
        const { storageId, reservations } = evt.payload as { storageId: string; reservations: StorageSnapshot };
        updateReservationCache(storageId, reservations);
      }
    });
  });
  onCleanup(() => unsubSSE?.());

  async function loadStorageData(id: string) {
    const [itemsResp, res] = await Promise.all([inv.fetchStorageItems(id), inv.fetchReservations(id)]);
    batch(() => {
      updateStorageCache(id, itemsResp.quantities);
      updateReservationCache(id, res);
      rememberStacks(itemsResp.stacks);
    });
  }

  async function selectPanelStorage(panel: "left" | "right", id: string) {
    await loadStorageData(id);
    if (panel === "left") setLeftStorageId(id);
    else setRightStorageId(id);
    if (selectedPanel() === panel) clearSelection();
  }

  const [leftScanLoading, setLeftScanLoading] = createSignal(false);
  const [rightScanLoading, setRightScanLoading] = createSignal(false);

  async function runLagerscan(panel: "left" | "right") {
    const sid = panel === "left" ? leftStorageId() : rightStorageId();
    if (!sid) return;
    const setLoading = panel === "left" ? setLeftScanLoading : setRightScanLoading;
    setLoading(true);
    try {
      const res = await api.inventory.runReadContents(sid, { botId: null });
      if (isOk(res.data)) toast?.info(t("INVENTORY_LAGERSCAN_QUEUED"));
      else toast?.error(firstError(res.data) ?? t("INVENTORY_LAGERSCAN_FAILED"));
    } catch (e: any) {
      toast?.error(e?.message ?? t("INVENTORY_LAGERSCAN_FAILED"));
    } finally {
      setLoading(false);
    }
  }

  async function handleCraft(key: string, times: number) {
    const panel = selectedPanel();
    const sid = panel === "left" ? leftStorageId() : rightStorageId();
    if (!sid) return;
    const params = new URLSearchParams({ item: key, source: sid, times: String(Math.max(1, Math.ceil(times))) });
    navigate(`/inventory/craft?${params.toString()}`);
  }

  function setPricingField(key: string, field: "buy" | "sell", raw: string) {
    const val = raw === "" ? undefined : Number(raw);
    const all = { ...pricing() };
    all[key] = { ...all[key], [field]: val };
    setPricingSig(all);
    inv.savePricing({ [key]: all[key] });
  }

  async function toggleDualView() {
    const next = !dualView();
    setDualView(next);
    if (next) {
      const rid = rightStorageId();
      if (!rid) {
        const list = storages();
        const lid = leftStorageId();
        const other = list.find((s) => s.id !== lid) ?? list[0];
        if (other) await selectPanelStorage("right", other.id);
      } else {
        await loadStorageData(rid);
      }
    }
  }

  function handleDragStart(e: DragEvent, itemKey: string, panel: "left" | "right") {
    const storageId = panel === "left" ? leftStorageId() : rightStorageId();
    if (!storageId) return;

    // Dragging a tile that is part of the selection takes the whole selection; dragging any other
    // tile drags that one, the way a file manager does.
    const keys = selectedPanel() === panel && selectedKeys().has(itemKey)
      ? selectedKeys()
      : new Set([itemKey]);
    const items = panelSlots(panel)
      .filter(s => keys.has(s.item.key) && s.available > 0)
      .map(s => ({ itemKey: s.item.key, available: s.available, stack: s.stack }));
    if (items.length === 0) return;

    e.dataTransfer!.effectAllowed = "copyMove";
    e.dataTransfer!.setData("text/plain", items.map(i => i.itemKey).join(","));
    if (dualView()) setDragData({ sourcePanel: panel, storageId, items });
  }

  function handleDragOver(e: DragEvent, panel: "left" | "right") {
    const drag = dragData();
    if (!drag || drag.sourcePanel === panel) return;
    e.preventDefault();
    e.dataTransfer!.dropEffect = "move";
    setDropTarget(panel);
  }

  function handleDragLeave(e: DragEvent, panel: "left" | "right") {
    const related = e.relatedTarget as HTMLElement | null;
    const panelEl = (e.currentTarget as HTMLElement);
    if (related && panelEl.contains(related)) return;
    if (dropTarget() === panel) setDropTarget(null);
  }

  async function handleDrop(e: DragEvent, panel: "left" | "right") {
    e.preventDefault();
    setDropTarget(null);
    const drag = dragData();
    setDragData(null);
    if (!drag || drag.sourcePanel === panel) return;

    const targetStorageId = panel === "left" ? leftStorageId() : rightStorageId();
    if (!targetStorageId) return;

    const fromName = storages().find(s => s.id === drag.storageId)?.name ?? "";
    const toName = storages().find(s => s.id === targetStorageId)?.name ?? "";

    setPendingMove({
      sourceStorageId: drag.storageId,
      sourceStorageName: fromName,
      destinationStorageId: targetStorageId,
      destinationStorageName: toName,
      items: drag.items,
    });
  }

  async function confirmPendingMove(rows: { itemKey: string; quantity: number }[], botId: string | null) {
    const pending = pendingMove();
    if (!pending) return;
    if (await queueMoves(pending.sourceStorageId, pending.destinationStorageId, rows, botId)) setPendingMove(null);
  }

  async function queueMoves(
    sourceStorageId: string,
    targetStorageId: string,
    moves: { itemKey: string; quantity: number }[],
    botId?: string | null,
  ): Promise<boolean> {
    const quantityByItemKey = new Map<string, number>();
    for (const move of moves) quantityByItemKey.set(move.itemKey, (quantityByItemKey.get(move.itemKey) ?? 0) + move.quantity);
    const lines = [...quantityByItemKey].map(([itemKey, quantity]) => ({ itemKey, quantity, itemDisplayName: itemName(itemKey) }));

    try {
      const queued = await inv.createMoveRequest(sourceStorageId, targetStorageId, lines, (botId ?? preferredBotId()) || undefined);
      if (queued.length === 1) toast?.info(t("INVENTORY_MOVE_QUEUED_ONE"));
      else toast?.info(t("INVENTORY_MOVES_QUEUED", queued.length));
      return true;
    } catch (e: any) {
      toast?.error(e?.message ?? t("INVENTORY_MOVE_FAILED"));
      return false;
    }
  }

  function handleDragEnd() {
    setDragData(null);
    setDropTarget(null);
  }

  const activeSid = () => selectedPanel() === "left" ? leftStorageId() : rightStorageId();

  const [activeChests] = createResource(activeSid, (sid) => inv.fetchAllChests(sid));

  return (
    <div class={styles.container}>
      <div class={styles.pageHeader}>
        <h1><Text val="INVENTORY" /></h1>
        <div class="h" style={{ "align-items": "center", gap: "var(--spacing-sm)" }}>
          <button
            class={`${styles.storageBtn} ${styles.dualViewToggle} ${dualView() ? styles.dualViewActive : ""}`}
            onClick={toggleDualView}
            title={dualView() ? t("INVENTORY_SINGLE_VIEW") : t("INVENTORY_DUAL_VIEW")}
          >
            <i class={`fa-solid ${dualView() ? "fa-table-columns" : "fa-table-cells-large"}`} />
          </button>
        </div>
      </div>

      <Show when={loading()}>
        <div class={styles.loading}><Text val="INVENTORY_LOADING" /></div>
      </Show>

      <Show when={!loading() && storages().length === 0}>
        <StateDisplay empty icon="fa-solid fa-warehouse" message="INVENTORY_NO_STORAGES" helpText="INVENTORY_NO_STORAGES_HELP" />
      </Show>

      <Show when={!loading() && storages().length > 0 && resolver()}>
        <div class={`${styles.layout} ${dualView() ? styles.layoutDual : ""}`}>
          <div class={`${styles.panelsRow} ${dualView() ? styles.panelsDual : ""}`}>
            <InventoryPanel
              panel="left"
              storageId={leftStorageId}
              slots={leftSlots}
              search={leftSearch}
              setSearch={setLeftSearch}
              sort={leftSort}
              setSort={setLeftSort}
              storages={storages}
              selectPanelStorage={selectPanelStorage}
              dragData={dragData}
              dropTarget={dropTarget}
              handleDragStart={handleDragStart}
              handleDragOver={handleDragOver}
              handleDragLeave={handleDragLeave}
              handleDrop={handleDrop}
              handleDragEnd={handleDragEnd}
              selected={selected}
              selectedKeys={selectedKeys}
              selectedPanel={selectedPanel}
              onSelectItem={selectItem}
              changedKeys={changedKeys}
              onLagerscan={() => runLagerscan("left")}
              lagerscanLoading={leftScanLoading}
            />
            <Show when={dualView()}>
              <InventoryPanel
                panel="right"
                storageId={rightStorageId}
                slots={rightSlots}
                search={rightSearch}
                setSearch={setRightSearch}
                sort={rightSort}
                setSort={setRightSort}
                storages={storages}
                selectPanelStorage={selectPanelStorage}
                dragData={dragData}
                dropTarget={dropTarget}
                handleDragStart={handleDragStart}
                handleDragOver={handleDragOver}
                handleDragLeave={handleDragLeave}
                handleDrop={handleDrop}
                handleDragEnd={handleDragEnd}
                selected={selected}
                selectedKeys={selectedKeys}
                selectedPanel={selectedPanel}
                onSelectItem={selectItem}
                changedKeys={changedKeys}
                onLagerscan={() => runLagerscan("right")}
                lagerscanLoading={rightScanLoading}
              />
            </Show>
          </div>

          <div class={styles.rightPane}>
          <Show when={selected()}>
            {(sel) => (
              <ItemDetailCard
                selected={sel()}
                selectedStack={selectedStack()}
                activeSid={activeSid}
                activeStorageName={() => storages().find(s => s.id === activeSid())?.name ?? "Storage"}
                activeStorageChests={() => activeChests() ?? []}
                storageCache={storageCache}
                reservationCache={reservationCache}
                allStorage={allStorage}
                pricing={pricing}
                profitMargin={profitMargin}
                resolver={resolver()!}
                storages={storages}
                handleCraft={handleCraft}
                onPricingChange={setPricingField}
                onMoveSuccess={(msg) => toast?.success(msg)}
                onMoveError={(msg) => toast?.error(msg)}
              />
            )}
          </Show>
          </div>
        </div>
      </Show>

      <Show when={!loading() && storages().length > 0 && resolver()}>
        <CraftingTaskPanel resolver={resolver()!} />
      </Show>

      <Show when={pendingMove()}>
        {(pending) => (
          <MoveConfirmDialog
            open={true}
            onClose={() => setPendingMove(null)}
            sourceStorageId={pending().sourceStorageId}
            sourceStorageName={pending().sourceStorageName}
            destinationStorageId={pending().destinationStorageId}
            destinationStorageName={pending().destinationStorageName}
            items={pending().items}
            resolver={resolver()}
            itemName={itemName}
            onConfirm={confirmPendingMove}
          />
        )}
      </Show>

    </div>
  );
}
